import cloneDeep from 'lodash/cloneDeep';
import type { ActorRef, ActorRefFrom, Snapshot } from 'xstate';
import { assign, sendTo, setup } from 'xstate';

import type { QuestionResponseModel } from '@webapp/common/resources/entities';
import { initialQuestionResponse } from '@webapp/common/resources/entities';

import type { SavedQuestionResponse } from '@webapp/survey/src/machine/lib';

export type ResponseEventParams = {
    questionId: number;
    answerId?: number;
    groupId?: number;
    value: PrimitiveValue | string;
};

export type QuestionResponseEvent =
    | { type: 'CLEAR' }
    | {
          type: 'CHANGE';
          value: PrimitiveValue;
          questionId?: number;
          answerId?: number;
          groupId?: number;
      }
    | { type: 'CHANGE_EXTRA'; value: PrimitiveValue }
    | { type: 'CHANGE_COMMENT'; value: string }
    | { type: 'SET'; value: PrimitiveValue }
    | { type: 'SET_EXTRA'; value: PrimitiveValue }
    | { type: 'SET_COMMENT'; value: string };

export enum QuestionResponseState {
    ready = 'ready',
}

type ChildEvent =
    | (ResponseEventParams & {
          type: 'RESPONSE_CHANGE';
      })
    | (ResponseEventParams & {
          type: 'RESPONSE_CHANGE_COMMENT';
      })
    | (ResponseEventParams & {
          type: 'RESPONSE_CHANGE_EXTRA';
      });

type ParentActor = ActorRef<Snapshot<unknown>, ChildEvent>;

export interface QuestionResponseContext extends QuestionResponseModel {
    questionId: number;
    answerId?: number;
    groupId?: number;
    dirty: boolean;
    parentRef: ParentActor;
}

export interface QuestionResponseInput {
    questionId: number;
    answerId?: number;
    groupId?: number;
    parentRef: ParentActor;
    savedResponse: SavedQuestionResponse;
}

export const QuestionResponseMachine = setup({
    types: {
        context: {} as QuestionResponseContext,
        events: {} as QuestionResponseEvent,
        input: {} as QuestionResponseInput,
    },
}).createMachine({
    id: 'QuestionResponseMachine',
    initial: QuestionResponseState.ready,
    context: ({ input: { savedResponse, ...input } }) => ({
        ...cloneDeep(initialQuestionResponse),
        ...input,
        ...savedResponse,
        dirty: false,
    }),
    states: {
        ready: {
            on: {
                CLEAR: {
                    actions: assign(() => {
                        const empty = cloneDeep(initialQuestionResponse);
                        return {
                            value: empty.value,
                            comment: empty.comment,
                            extra: empty.extra,
                        };
                    }),
                },
                SET: {
                    actions: assign(({ event }) => {
                        return { value: event.value };
                    }),
                },
                SET_EXTRA: {
                    actions: assign(({ event }) => {
                        return { extra: event.value };
                    }),
                },
                SET_COMMENT: {
                    actions: assign(({ event }) => {
                        return { comment: event.value };
                    }),
                },
                CHANGE: {
                    actions: [
                        sendTo(
                            ({ context }) => context.parentRef,
                            ({ context, event }) => ({
                                type: 'RESPONSE_CHANGE',
                                value: event.value,
                                questionId: context.questionId,
                                answerId: context.answerId,
                                groupId: context.groupId,
                            }),
                        ),
                        assign(({ event }) => {
                            return { value: event.value };
                        }),
                    ],
                },
                CHANGE_EXTRA: {
                    actions: [
                        sendTo(
                            ({ context }) => context.parentRef,
                            ({ context, event }) => ({
                                type: 'RESPONSE_CHANGE_EXTRA',
                                value: event.value,
                                questionId: context.questionId,
                                answerId: context.answerId,
                                groupId: context.groupId,
                            }),
                        ),
                        assign(({ event }) => {
                            return { extra: event.value };
                        }),
                    ],
                },
                CHANGE_COMMENT: {
                    actions: [
                        assign(({ event }) => {
                            return { comment: event.value };
                        }),
                        sendTo(
                            ({ context }) => context.parentRef,
                            ({ event, context }) => ({
                                type: 'RESPONSE_CHANGE_COMMENT',
                                value: event.value,
                                questionId: context.questionId,
                                answerId: context.answerId,
                                groupId: context.groupId,
                            }),
                        ),
                    ],
                },
            },
        },
    },
});

export type QuestionResponseActor = ActorRefFrom<typeof QuestionResponseMachine>;
